import { Fragment, useState, type JSX } from 'react';
import type { VoiceChatViewerItemType } from 'types/api/voiceChatViewerRefResponseType';

const STATUS_LABEL: Record<number, string> = {
  10: '表示',
  20: '開始',
  30: '設問中',
  40: '完了',
};

const FILTER_CHIPS: { div: number; label: string }[] = [
  { div: 0, label: 'すべて' },
  { div: 10, label: '表示のみで離脱' },
  { div: 20, label: '開始以上' },
  { div: 30, label: '完了' },
  { div: 40, label: '離脱' },
];

/**
 * 視聴者一覧パネル（EV-vc-viewer-01/02/03）。
 * フィルタはサーバ側 where で絞り込み済み（クライアント絞り込みなし）。
 * 行内「到達履歴 ▸」で displayedAt→startedAt→completedAt→lastReachedAt を展開。
 */
export default function WsReportVoiceChatViewers(props: {
  viewers: VoiceChatViewerItemType[];
  filterDiv: number;
  onChangeFilter: (div: number) => void;
  onDownloadCsv: (encoding?: 'utf8' | 'shift_jis' | null) => void;
}): JSX.Element {
  const { viewers, filterDiv, onChangeFilter, onDownloadCsv } = props;
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="box-common shadow-none">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="title-section title-md mb-0">視聴者一覧</h3>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => onDownloadCsv('utf8')}
        >
          CSV ダウンロード（{viewers.length}件）
        </button>
      </div>

      {/* フィルタチップ */}
      <div className="d-flex flex-wrap mb-3" style={{ gap: 'var(--space-2)' }}>
        {FILTER_CHIPS.map(chip => (
          <button
            key={chip.div}
            type="button"
            className={`btn btn-sm${filterDiv === chip.div ? ' btn-primary' : ' btn-outline-secondary'}`}
            onClick={() => onChangeFilter(chip.div)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <span className="vcr-muted">全 {viewers.length} 件</span>
        <table className="vcr-table">
          <thead>
            <tr>
              <th>ID（tid / 匿名）</th>
              <th>種別</th>
              <th>到達段階</th>
              <th>到達設問</th>
              <th>状態</th>
              <th>最終アクセス</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {viewers.length === 0 && (
              <tr>
                <td colSpan={7} className="vcr-muted">
                  データがありません
                </td>
              </tr>
            )}
            {viewers.map(viewer => {
              const open = openId === viewer.trackingId;
              return (
                <Fragment key={viewer.trackingId}>
                  <tr>
                    <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {viewer.trackingId}
                    </td>
                    <td>{viewer.trackingIdTypeDiv === 10 ? 'パラメータ' : '匿名'}</td>
                    <td>{STATUS_LABEL[viewer.statusDiv] ?? '—'}</td>
                    <td>
                      {viewer.reachedQuestionNo != null && viewer.reachedQuestionNo >= 1
                        ? `設問${viewer.reachedQuestionNo}`
                        : '—'}
                    </td>
                    <td>{viewer.completedFlag ? '完了' : '離脱/進行中'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{viewer.lastReachedAt ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span
                        className="vcr-link"
                        onClick={() => setOpenId(open ? null : viewer.trackingId)}
                      >
                        {open ? '履歴を閉じる' : '到達履歴 ▸'}
                      </span>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={7}>
                        <div
                          className="d-flex flex-wrap"
                          style={{ gap: 'var(--space-2)', padding: 'var(--space-2)' }}
                        >
                          {[
                            { label: '表示', at: viewer.displayedAt },
                            { label: '開始', at: viewer.startedAt },
                            { label: '最終到達', at: viewer.lastReachedAt },
                            { label: '完了', at: viewer.completedAt },
                          ].map(item => (
                            <span key={item.label} className="badge bg-light text-dark border">
                              {item.label}：{item.at ?? '—'}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
